import { Component, type ErrorInfo, type ReactNode } from 'react';

interface IErrorBoundaryProps {
  children: ReactNode;
}

interface IErrorBoundaryState {
  error: Error | null;
}

/**
 * Top-level error boundary so a render crash shows a recoverable screen
 * instead of a blank page.
 */
class ErrorBoundary extends Component<
  IErrorBoundaryProps,
  IErrorBoundaryState
> {
  state: IErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): IErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Unhandled render error:', error, errorInfo.componentStack);
  }

  handleReload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    const { error } = this.state;
    const { children } = this.props;

    if (!error) {
      return children;
    }

    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 text-gray-600">
        <h1 className="text-xl font-semibold">Something went wrong</h1>
        <p className="text-sm text-gray-400 max-w-md text-center break-words">
          {error.message}
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          className="px-4 py-2 rounded-md bg-gray-900 text-white text-sm hover:bg-gray-700"
        >
          Reload page
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
